<template>
  <div>
    <page-header
      :page="headerPage"
      :actions="headerActions"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />

    <board class="mb-4" :details="boardDetails">
      <template #data-item="{ item }">
        <div v-if="!item.data">
          <custom-balloon
            :background-color="item.statusColor"
            :text="item.statusName"
            dot
          />
        </div>
      </template>
    </board>

    <div class="mb-4">
      <q-tab v-if="loaded" :active-tab="activeTab">
        <q-tab-item
          :lazy="loadComponent(key)"
          v-for="(tab, key) in tabs"
          :key="key"
          :title="tab.title"
          :has-error="!tab.valid"
          :visible="tab.visible"
          :nav-item-class="tab.classes"
          @click="setCurrentTab(key)"
        >
          <component
            :key="tabKey"
            :referral-id="referralId"
            :is-read-only="isReadOnly"
            :is="tab.id"
            :ref="tab.id"
            :code="tab.id"
            :data="tab.data"
            :referral="referral"
            @loaded="setTabLoaded"
          ></component>
        </q-tab-item>
      </q-tab>
    </div>

    <div class="d-flex justify-content-end" v-if="!isReadOnly">
      <action-button
        variant="outline-success"
        @click="handleSubmit"
        type="button"
        i="fas fa-save"
      >
        Guardar
      </action-button>
    </div>
    <div class="d-flex justify-content-end" v-if="isReadOnly && canEdit">
      <action-button
        variant="outline-success"
        @click="handleEdit"
        type="button"
        i="fas fa-edit"
      >
        Editar Evaluación
      </action-button>
    </div>
  </div>
</template>
<script>
import PageHeader from 'PageHeader';
import Board from 'Board';
import ActionButton from 'ActionButton';
import { QTab, QTabItem } from '@/components/common/q-tabs';
import mixAlert from '@/mixins/alert.mixin';
import { date } from '@/utils/filters';
import referralService from '@/services/api/referralsandequipments.api';
import peiATServiceStatus from '@/utils/constants/peiATServiceStatus';
import peiAtReferralType from '@/utils/constants/peiAtReferralType';
import CustomBalloon from 'custom-balloon';

import {
  TabI_II,
  TabIIIa,
  TabIIIb,
  TabIV,
  TabV,
  TabVI,
  TabVII,
  TabDocuments,
} from './tabs';
import TABS from '@/utils/constants/evaluationTabs';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const REFERRAL_EQUIPMENT_ROUTE_NAME = 'referralsandequipment';

export default {
  components: {
    PageHeader,
    Board,
    QTab,
    QTabItem,
    ActionButton,
    'Tab-I-II': TabI_II,
    'Tab-IIIa': TabIIIa,
    'Tab-IIIb': TabIIIb,
    'Tab-IV': TabIV,
    'Tab-V': TabV,
    'Tab-VI': TabVI,
    'Tab-VII': TabVII,
    'Tab-Documents': TabDocuments,
    CustomBalloon,
  },
  mixins: [mixAlert],
  data: () => ({
    referral: {},
    activeTab: TABS.I_II,
    loaded: false,
    tabKey: 0,
    loading: false,
    tabsLoaded: [
      { id: TABS.I_II, lazy: true, loaded: false, visible: true },
      { id: TABS.IIIa, lazy: true, loaded: false, visible: true },
      { id: TABS.IIIb, lazy: true, loaded: false, visible: true },
      { id: TABS.IV, lazy: true, loaded: false, visible: true },
      { id: TABS.V, lazy: true, loaded: false, visible: true },
      { id: TABS.VI, lazy: true, loaded: false, visible: true },
      { id: TABS.VII, lazy: true, loaded: false, visible: true },
      { id: TABS.Documents, lazy: true, loaded: false, visible: false },
    ],
    atDocumentRequired: false,
  }),
  computed: {
    loadComponent() {
      return (tab) => {
        const item = this.tabsLoaded
          .filter((x) => x.visible)
          .find((x) => x.id == tab);
        return item?.lazy;
      };
    },
    referralId() {
      return Number(this.$route.params.id);
    },
    headerPage() {
      return {
        icon: 'fas fa-list',
        title: 'Informe de Evaluación en Asistencia Tecnológica',
        routes: [
          'Asistencia Tecnológica',
          'Manejo de Referidos y Equipos',
          'Evaluar',
        ],
      };
    },
    headerActions() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () =>
            this.$router.push({ name: REFERRAL_EQUIPMENT_ROUTE_NAME }),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'outline-success',
          visible: !this.isReadOnly,
          click: () => this.handleSubmit(),
        },
        {
          text: 'Someter',
          icon: 'fas fa-flag',
          variant: 'success',
          visible: !this.isReadOnly,
          click: () => this.handleSubmit(true),
        },
        {
          text: 'Editar Evaluación',
          icon: 'fas fa-edit',
          variant: 'outline-success',
          visible: this.isReadOnly && this.canEdit,
          click: () => this.handleEdit(),
        },
        {
          text: 'Enviar a Discusión',
          icon: 'fas fa-arrow-circle-right',
          variant: 'success',
          visible: this.showSendToDiscussion,
          click: () => this.sendToDiscussion(),
        },
      ];
    },
    boardDetails() {
      return [
        [
          {
            label: 'Estudiante:',
            icon: 'fas fa-user',
            data: `${this.referral.studentName} - ${this.referral.studentSie}`,
            cols: 'col-12 col-md-3 pt-2',
          },
          {
            label: 'Número de Referido:',
            icon: 'fas fa-file-user',
            data: this.referral.referralNumber || '-',
            cols: 'col-12 col-md pt-2',
          },
          {
            label: 'Fecha de Referido:',
            icon: 'fas fa-calendar-minus',
            data: date(this.referral.referralDate),
            cols: 'col-12 col-md pt-2',
          },
          {
            label: 'CAAT:',
            icon: 'fas fa-location-dot',
            data: this.referral.caat || '-',
            cols: 'col-12 col-md pt-2',
          },
          {
            label: 'Estatus:',
            icon: 'fas fa-file-lines',
            statusColor: this.referral.serviceStatusColor,
            statusName: this.referral.serviceStatusName,
            cols: 'col-12 col-md-3 pt-2',
          },
        ],
      ];
    },
    isReadOnly() {
      return this.referral?.isEvaluated;
    },
    tabs() {
      const allTabs = [
        {
          id: 'Tab-I-II',
          key: TABS.I_II,
          title: 'I-II',
          valid: true,
          visible: true,
          data: {},
        },
        {
          id: 'Tab-IIIa',
          key: TABS.IIIa,
          title: 'III.a',
          valid: true,
          visible: true,
          data: {},
        },
        {
          id: 'Tab-IIIb',
          key: TABS.IIIb,
          title: 'III.b',
          valid: true,
          visible: true,
          data: {
            referralInfo: {},
          },
        },
        {
          id: 'Tab-IV',
          key: TABS.IV,
          title: 'IV',
          valid: true,
          visible: true,
          data: {},
        },
        {
          id: 'Tab-V',
          key: TABS.V,
          title: 'V',
          valid: true,
          visible: true,
          data: {},
        },
        {
          id: 'Tab-VI',
          key: TABS.VI,
          title: 'VI',
          valid: true,
          visible: true,
          data: {},
        },
        {
          id: 'Tab-VII',
          key: TABS.VII,
          title: 'VII',
          valid: true,
          visible: true,
          data: {},
        },
        {
          id: 'Tab-Documents',
          key: TABS.Documents,
          title: 'Documentos',
          valid: true,
          visible: this.isReadOnly,
          data: {
            documentId: null,
            onDocumentChange: (id) => {
              if (this.atDocumentRequired) {
                this.tabs[TABS.Documents].valid = Boolean(id);
                this.$forceUpdate();
              }
            },
          },
        },
      ];

      if (
        [
          peiAtReferralType.Validation,
          peiAtReferralType.Appraisal,
          peiAtReferralType.EquipmentRelatedServiceRequest,
        ].includes(this.referral?.referralTypeId)
      ) {
        allTabs.forEach((tab, i) => {
          tab.visible = [TABS.I_II, TABS.V].includes(i);
          tab.visible = TABS.Documents == i ? this.isReadOnly : tab.visible;

          if (tab.visible) tab.classes = 'col-md-2 col-xs-12';
        });
        this.tabsLoaded.forEach((tab) => {
          tab.visible = [TABS.I_II, TABS.V].includes(tab.id);
          tab.visible =
            TABS.Documents == tab.id ? this.isReadOnly : tab.visible;
        });
      }
      return allTabs;
    },
    showSendToDiscussion() {
      return (
        this.isReadOnly &&
        [
          peiATServiceStatus.PendienteDeEvaluacion,
          peiATServiceStatus.PendienteDocumentarEvaluacion,
          peiATServiceStatus.PendienteDeValidacion,
          peiATServiceStatus.PendienteDeAvaluo,
        ].includes(this.referral?.serviceStatusId)
      );
    },
    canEdit() {
      return [
        peiATServiceStatus.PendienteDeEvaluacion,
        peiATServiceStatus.PendienteDocumentarEvaluacion,
        peiATServiceStatus.PendienteDeValidacion,
        peiATServiceStatus.PendienteDeAvaluo,
      ].includes(this.referral?.serviceStatusId);
    },
  },
  async mounted() {
    await this.initData();
    this.loaded = true;
  },
  methods: {
    async initData() {
      await this.getBoardInfo();
    },
    setTabLoaded(i) {
      if (i == undefined) {
        return;
      }
      const tab = this.tabsLoaded
        .filter((x) => x.visible)
        .find((x) => x.id == i);
      tab.lazy = false;
      tab.loaded = true;
    },
    async getBoardInfo() {
      const { data } = await referralService.getReferralSummary(
        this.referralId
      );
      this.referral = data;
      this.tabs[TABS.IIIb].data.referralInfo = this.referral;
    },
    async setCurrentTab(tab) {
      this.activeTab = tab;
    },
    async validate() {
      const refs = this.tabs
        .filter((x) => x.visible)
        .map(({ id }) => this.$refs[id][0]);

      const validations = refs.map(({ validate }) => validate());
      const results = await Promise.allSettled(validations);
      results.forEach(({ value }, i) => (this.tabs[i].valid = value?.valid));
      this.$forceUpdate();
      const areValid = results.every(({ value }, i) => {
        const { valid, message } = value;
        if (!valid)
          this.ShowToast(
            `Error en el TAB ${this.tabs.filter((x) => x.visible)[i].title}`,
            message,
            'error'
          );
        return valid;
      });
      return areValid;
    },
    async handleSubmit(submitted = false) {
      const refs = this.tabs
        .filter((x) => x.visible)
        .map(({ id }) => this.$refs[id]);

      const index = this.tabs
        .filter((x) => x.visible)
        .findIndex((x) => x.key == this.activeTab);

      await refs[index][0].save(!submitted);

      if (submitted) {
        this.tabsLoaded
          .filter((x) => x.visible)
          .forEach((element) => {
            if (!element.lazy) {
              return;
            }
            element.lazy = false;
          });

        await this.verifyLoaded();
        const valid = await this.validate();
        if (!valid) return;

        const isConfirmed = await this.ShowCancelModal(
          'Confirmación',
          `¿Está seguro que desea someter la evaluación? (190149)`,
          'Sí',
          'No'
        );
        if (isConfirmed) {
          referralService
            .submitEvaluation(this.referralId)
            .then(async () => {
              this.ShowToast(
                'Éxito',
                'Información ha sido sometida exitosamente.',
                'success'
              );

              await this.initData();
            })
            .catch((error) => {
              this.ShowToast('Alerta', error?.response?.data.message, 'error');
            });
        }
      }
    },
    async sendToDiscussion() {
      try {
        const hasSingedDocument = Boolean(
          this.tabs[TABS.Documents].data?.documentId
        );
        if (!hasSingedDocument) {
          this.atDocumentRequired = true;
          this.tabs[TABS.Documents].valid = false;
          this.$forceUpdate();
          this.ShowToast(
            'Alerta',
            `El documento Reporte de Evaluación de Asistencia Tecnológica Firmado es necesario para Enviar a Discusión. (190158)`,
            'error'
          );
          return;
        }

        const hasType = [
          peiAtReferralType.Regular,
          peiAtReferralType.TechnicalAssistanceServiceRequest,
        ].includes(this.referral.referralTypeId);

        if (hasType) {
          const isConfirmed = await this.ShowCancelModal(
            'Confirmación',
            `Se identificó la participación de un especialista de corporación privada  por lo que se creará un record para la facturación. ¿Desea continuar con la acción? (R03200001)`,
            'Sí',
            'No'
          );
          if (!isConfirmed) return;
        }

        const isConfirmed = await this.ShowCancelModal(
          'Confirmación',
          `¿Está seguro que desea cambiar el estatus del referido de ${this.referral.serviceStatusName} a Pendiente de Discusión?`,
          'Sí',
          'No'
        );
        if (!isConfirmed) return;

        await referralService.changeStatus(this.referralId, {
          statusId: peiATServiceStatus.PendienteDeDiscusion,
        });

        this.ShowToast(
          'Éxito',
          'El estatus ha sido cambiado exitosamente.',
          'success'
        );

        await this.initData();
      } catch (error) {
        this.ShowToast(
          'Error',
          'Ha ocurrido un error al cambiar el estatus del referido',
          'error'
        );
      }
    },
    async verifyLoaded() {
      const existPendingTab = this.tabsLoaded
        .filter((x) => x.visible)
        .some((x) => x.loaded === false);

      if (!existPendingTab) {
        return;
      }

      await sleep(500);
      await this.verifyLoaded();
    },
    async handleEdit() {
      this.atDocumentRequired = false;
      this.tabs[TABS.Documents].valid = true;
      this.$forceUpdate();
      const hasSingedDocument = Boolean(
        this.tabs[TABS.Documents].data?.documentId
      );
      const message = hasSingedDocument
        ? `¿Está seguro que desea modificar la Evaluación Sometida?
          <br/> <br/><b>Nota</b>: De tener la Evaluación AT Firmada en sistema, se borrará la misma y debe ser cargada nuevamente con los cambios y firmas correspondientes. (200102)`
        : `¿Está seguro que desea editar la evaluación? (190149)`;

      const isConfirmed = await this.ShowCancelModal(
        'Confirmación',
        message,
        'Sí',
        'No'
      );
      if (isConfirmed) {
        referralService
          .editEvaluation(this.referralId)
          .then(async () => {
            this.ShowToast(
              'Éxito',
              'Información ha sido editada exitosamente.',
              'success'
            );

            await this.initData();
          })
          .catch((error) => {
            this.ShowToast('Alerta', error?.response?.data.message, 'error');
          });
      }
    },
  },
};
</script>
<style scoped lang="scss">
::v-deep .font-weight-bold {
  font-weight: 400 !important;
}
</style>
